import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BookOpen, Plus, Trash2, Undo2, CheckCircle2, XCircle, Scale, ChevronRight,
  Upload, FileSpreadsheet, Download,
} from "lucide-react";

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function JournalEntryPage() {
  const { currentProperty, propertyId } = useProperty();
  const pid = currentProperty?.id || propertyId;
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const [je, coa] = await Promise.all([
        api.get("/admin/gl/journal-entries", { params: { property_id: pid, limit: 200 } }),
        api.get("/admin/coa", { params: { property_id: pid } }),
      ]);
      setEntries(je.data.items || []);
      setTotals(je.data.totals || {});
      setAccounts(coa.data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setLoading(false);
  }, [pid]);

  useEffect(() => { load(); }, [load]);

  const reverse = async (id) => {
    if (!window.confirm("Post a reversing entry? The original stays intact for the audit trail.")) return;
    try {
      await api.post(`/admin/gl/journal-entry/${id}/reverse`);
      toast.success("Reversal posted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const hardDelete = async (id) => {
    if (!window.confirm("Delete this JE permanently? Only same-day typos should be deleted; older entries must be reversed.")) return;
    try {
      await api.delete(`/admin/gl/journal-entry/${id}`);
      toast.success("Entry deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="space-y-6" data-testid="je-page">
      <PageHeader
        eyebrow="General Ledger"
        title="Journal Entries"
        subtitle="Balanced double-entry postings that hit specific Chart of Accounts codes. Every line is a debit or credit — never both."
        actions={
          <div className="flex flex-wrap gap-2">
            {accounts.length === 0 ? (
              <Badge variant="outline" className="border-amber-500/40 text-amber-300">
                Seed your Chart of Accounts first at /admin/coa
              </Badge>
            ) : (
              <>
                <BulkImportDialog propertyId={pid} accounts={accounts} onImported={load} />
                <NewEntryDialog propertyId={pid} accounts={accounts} onCreated={load} />
              </>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Posted entries" value={totals.count || 0} sub="last 200 shown" />
        <Kpi label="Total debits" value={money(totals.debits)} tone="bad" />
        <Kpi label="Total credits" value={money(totals.credits)} tone="good" />
      </div>

      {loading && entries.length === 0 && <div className="text-sm text-zinc-500">Loading…</div>}

      {entries.length === 0 && !loading && (
        <Card className="border-white/10 bg-card p-10 text-center" data-testid="je-empty">
          <Scale className="h-8 w-8 mx-auto mb-3 text-zinc-500" />
          <div className="text-lg font-semibold">No journal entries yet</div>
          <div className="text-sm text-zinc-400 mt-2 mb-4">
            Post your first balanced entry to correct a rent posting, accrue an expense, or reclassify a line.
          </div>
          {accounts.length > 0 && <NewEntryDialog propertyId={pid} accounts={accounts} onCreated={load} />}
        </Card>
      )}

      <div className="space-y-3">
        {entries.map((e) => (
          <EntryCard key={e.id} entry={e} onReverse={() => reverse(e.id)} onDelete={() => hardDelete(e.id)} />
        ))}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone = "default" }) {
  const cls = {
    default: "border-white/10 bg-card",
    good: "border-emerald-500/20 bg-emerald-500/[0.03]",
    bad: "border-rose-500/20 bg-rose-500/[0.03]",
  }[tone];
  return (
    <Card className={`p-4 ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-2xl font-semibold font-display mt-1">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </Card>
  );
}

function EntryCard({ entry, onReverse, onDelete }) {
  const isReversal = !!entry.reverses_journal_entry_id;
  const wasReversed = !!entry.reversed_by;
  return (
    <Card className="border-white/10 bg-card p-4" data-testid={`je-card-${entry.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-[11px] font-mono text-zinc-500">{entry.date}</div>
            <div className="font-semibold text-sm">{entry.memo}</div>
            {isReversal && <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px]"><Undo2 className="h-3 w-3 mr-1" />Reversal</Badge>}
            {wasReversed && <Badge variant="outline" className="border-white/15 text-zinc-500 text-[10px] line-through">reversed</Badge>}
          </div>
          <div className="text-[10px] text-zinc-500 mt-1 font-mono">
            #{entry.id.slice(0, 8)} · {entry.created_by_name || "system"}
          </div>
        </div>
        <div className="flex gap-1">
          {!wasReversed && !isReversal && (
            <>
              <Button size="sm" variant="ghost" onClick={onReverse}
                      className="text-xs h-7 gap-1" data-testid={`je-reverse-${entry.id}`}>
                <Undo2 className="h-3 w-3" /> Reverse
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete}
                      className="text-xs h-7 gap-1 text-rose-400 hover:text-rose-300"
                      data-testid={`je-delete-${entry.id}`}>
                <Trash2 className="h-3 w-3" /> Delete
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-2 py-1 w-20">Code</th>
              <th className="text-left px-2 py-1">Account</th>
              <th className="text-left px-2 py-1">Memo</th>
              <th className="text-right px-2 py-1 w-24">Debit</th>
              <th className="text-right px-2 py-1 w-24">Credit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {entry.lines.map((l) => (
              <tr key={l.id} className="hover:bg-white/[0.02]">
                <td className="px-2 py-1 font-mono text-zinc-500">{l.account_code}</td>
                <td className="px-2 py-1">{l.account_name}</td>
                <td className="px-2 py-1 text-zinc-400">{l.memo || ""}</td>
                <td className="px-2 py-1 text-right font-mono text-rose-300">{l.debit > 0 ? money(l.debit) : ""}</td>
                <td className="px-2 py-1 text-right font-mono text-emerald-300">{l.credit > 0 ? money(l.credit) : ""}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-white/10 text-[11px] font-semibold">
            <tr>
              <td colSpan={3} className="px-2 py-1 text-right text-zinc-400">Totals</td>
              <td className="px-2 py-1 text-right font-mono">{money(entry.total_debits)}</td>
              <td className="px-2 py-1 text-right font-mono">{money(entry.total_credits)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}

function BulkImportDialog({ propertyId, accounts, onImported }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setCsv(""); setPreview(null); }
  }, [open]);

  const template = [
    "entry_ref,date,memo,ref,account_code,debit,credit,line_memo",
    "JE001,2026-02-08,Reclass Feb utilities,,5100,150.00,,Electricity",
    "JE001,2026-02-08,Reclass Feb utilities,,5110,,150.00,Utilities expense reclass",
    "JE002,2026-02-08,Accrue vendor bill,INV#123,1200,,825.50,AP accrual",
    "JE002,2026-02-08,Accrue vendor bill,INV#123,6100,825.50,,Repairs & maintenance",
  ].join("\n");

  const downloadTemplate = () => {
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "journal-entries-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const parseCsv = (text) => {
    const rows = [];
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return { rows: [], parseError: "empty file" };
    const [headerLine, ...bodyLines] = lines;
    const headers = headerLine.split(",").map((h) => h.trim().toLowerCase());
    const required = ["entry_ref", "date", "memo", "account_code"];
    const missing = required.filter((c) => !headers.includes(c));
    if (missing.length) return { rows: [], parseError: `missing columns: ${missing.join(", ")}` };

    for (let i = 0; i < bodyLines.length; i += 1) {
      const cells = bodyLines[i].split(",");
      const row = {};
      headers.forEach((h, idx) => { row[h] = (cells[idx] || "").trim(); });
      const debit = Number(row.debit || 0);
      const credit = Number(row.credit || 0);
      rows.push({
        entry_ref: row.entry_ref,
        date: row.date,
        memo: row.memo,
        ref: row.ref || null,
        account_code: row.account_code,
        debit: Number.isFinite(debit) ? debit : 0,
        credit: Number.isFinite(credit) ? credit : 0,
        line_memo: row.line_memo || null,
      });
    }
    return { rows, parseError: null };
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsv(String(ev.target?.result || ""));
    reader.readAsText(file);
  };

  const runDryRun = async () => {
    if (!propertyId) return toast.error("Pick a property first");
    const { rows, parseError } = parseCsv(csv);
    if (parseError) return toast.error(parseError);
    if (!rows.length) return toast.error("no rows in CSV");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/gl/journal-entries/bulk-import", {
        property_id: propertyId, dry_run: true, rows,
      });
      setPreview(data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  const commit = async () => {
    if (!preview) return;
    const { rows } = parseCsv(csv);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/gl/journal-entries/bulk-import", {
        property_id: propertyId, dry_run: false, rows,
      });
      toast.success(`Posted ${data.posted_count} journal entries · ${(data.will_skip || []).length} skipped`);
      setOpen(false);
      onImported?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-white/10 gap-2" data-testid="je-bulk-import-btn">
          <Upload className="h-4 w-4" /> Bulk import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="je-bulk-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import journal entries</DialogTitle>
          <DialogDescription className="text-xs">
            One CSV row per line. Group rows into entries with the <span className="font-mono">entry_ref</span> column —
            every group is validated for balanced debits/credits before posting.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 mb-2">
          <Button size="sm" variant="ghost" onClick={downloadTemplate}
                  className="gap-1 text-xs h-7" data-testid="je-bulk-template">
            <Download className="h-3 w-3" /> Download template
          </Button>
          <label className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-white/10 hover:bg-white/5 cursor-pointer">
            <FileSpreadsheet className="h-3 w-3" />
            <span>Load CSV file</span>
            <input type="file" accept=".csv,text/csv" className="hidden"
                   onChange={onFile} data-testid="je-bulk-file" />
          </label>
        </div>

        <Textarea rows={10} value={csv} onChange={(e) => setCsv(e.target.value)}
                  placeholder={template}
                  className="font-mono text-[11px] bg-white/[0.02] border-white/10"
                  data-testid="je-bulk-textarea" />

        <div className="flex justify-between items-center mt-3">
          <Button size="sm" variant="outline" onClick={runDryRun} disabled={busy || !csv.trim()}
                  className="border-white/10 gap-1"
                  data-testid="je-bulk-preview">
            <Scale className="h-3.5 w-3.5" /> Preview (dry run)
          </Button>
          <div className="text-[10px] text-zinc-500">
            {accounts.length} account codes available on this property
          </div>
        </div>

        {preview && (
          <Card className="border-white/10 bg-card p-3 mt-3" data-testid="je-bulk-preview-card">
            <div className="text-xs font-semibold mb-2">Preview · {preview.total_groups} group(s)</div>
            <div className="grid grid-cols-3 gap-2 text-[11px] mb-3">
              <div className="rounded bg-emerald-500/[0.06] border border-emerald-500/30 p-2">
                <div className="text-emerald-300 font-semibold">Will post</div>
                <div className="text-lg">{(preview.will_create || []).length}</div>
              </div>
              <div className="rounded bg-amber-500/[0.06] border border-amber-500/30 p-2">
                <div className="text-amber-300 font-semibold">Skip</div>
                <div className="text-lg">{(preview.will_skip || []).length}</div>
              </div>
              <div className="rounded bg-rose-500/[0.06] border border-rose-500/30 p-2">
                <div className="text-rose-300 font-semibold">Errors</div>
                <div className="text-lg">{(preview.errors || []).length}</div>
              </div>
            </div>
            {(preview.errors || []).slice(0, 5).map((err) => (
              <div key={err.entry_ref} className="text-[11px] text-rose-300 mb-1">
                <span className="font-mono">{err.entry_ref}</span> · {(err.errors || []).slice(0, 2).join(" · ")}
              </div>
            ))}
            {(preview.errors || []).length > 5 && (
              <div className="text-[10px] text-zinc-500">…and {preview.errors.length - 5} more issue(s)</div>
            )}
          </Card>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10">Cancel</Button>
          <Button onClick={commit} disabled={busy || !preview || !(preview.will_create || []).length}
                  className="bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="je-bulk-commit">
            {busy ? "Posting…" : `Post ${(preview?.will_create || []).length || ""} entries`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewEntryDialog({ propertyId, accounts, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: today, memo: "", ref: "" });
  const [lines, setLines] = useState([
    { account_code: "", debit: "", credit: "", memo: "" },
    { account_code: "", debit: "", credit: "", memo: "" },
  ]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({ date: today, memo: "", ref: "" });
      setLines([
        { account_code: "", debit: "", credit: "", memo: "" },
        { account_code: "", debit: "", credit: "", memo: "" },
      ]);
    }
  }, [open, today]);

  const updateLine = (i, patch) => {
    setLines((cur) => cur.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  };

  const totals = useMemo(() => {
    let d = 0, c = 0;
    lines.forEach((l) => {
      d += Number(l.debit) || 0;
      c += Number(l.credit) || 0;
    });
    return { debits: d, credits: c, balanced: Math.abs(d - c) < 0.005 && d > 0 };
  }, [lines]);

  const addLine = () => setLines((cur) => [...cur, { account_code: "", debit: "", credit: "", memo: "" }]);
  const removeLine = (i) => setLines((cur) => cur.length > 2 ? cur.filter((_, j) => j !== i) : cur);

  const submit = async () => {
    if (!propertyId) return toast.error("Pick a property first");
    if (!form.memo.trim()) return toast.error("Memo is required");
    if (!totals.balanced) return toast.error(`Out of balance: debits ${money(totals.debits)} ≠ credits ${money(totals.credits)}`);
    const validLines = lines.filter((l) => l.account_code && (Number(l.debit) > 0 || Number(l.credit) > 0));
    if (validLines.length < 2) return toast.error("Need at least 2 posted lines");
    setBusy(true);
    try {
      await api.post("/admin/gl/journal-entry", {
        property_id: propertyId,
        date: form.date, memo: form.memo, ref: form.ref || null,
        lines: validLines.map((l) => ({
          account_code: l.account_code,
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0,
          memo: l.memo || null,
        })),
      });
      toast.success("Journal entry posted");
      setOpen(false);
      onCreated?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-orange-500 hover:bg-orange-600 gap-2" data-testid="je-new-btn">
          <Plus className="h-4 w-4" /> New journal entry
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="je-new-dialog">
        <DialogHeader>
          <DialogTitle>New journal entry</DialogTitle>
          <DialogDescription className="text-xs">
            Debits must equal credits. Every line must reference a Chart of Accounts code. Reversals stay editable for 24h.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Date</Label>
            <Input type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   data-testid="je-form-date" />
          </div>
          <div className="col-span-2">
            <Label>Memo</Label>
            <Input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })}
                   placeholder="Reclass Aug utilities to correct account" data-testid="je-form-memo" />
          </div>
          <div className="col-span-3">
            <Label>Reference (optional)</Label>
            <Input value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })}
                   placeholder="INV #12345 / Board minutes 2026-02-15" data-testid="je-form-ref" />
          </div>
        </div>

        <div className="mt-4">
          <div className="text-sm font-semibold mb-2">Lines</div>
          <div className="rounded-md border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-2 py-1 w-64">Account</th>
                  <th className="text-left px-2 py-1">Memo (opt)</th>
                  <th className="text-right px-2 py-1 w-28">Debit</th>
                  <th className="text-right px-2 py-1 w-28">Credit</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {lines.map((l, i) => (
                  <tr key={i} className="hover:bg-white/[0.02]" data-testid={`je-line-${i}`}>
                    <td className="px-2 py-1">
                      <Select value={l.account_code} onValueChange={(v) => updateLine(i, { account_code: v })}>
                        <SelectTrigger className="bg-white/[0.02] border-white/10 h-8 text-xs" data-testid={`je-line-account-${i}`}>
                          <SelectValue placeholder="Choose account…" />
                        </SelectTrigger>
                        <SelectContent className="max-h-96">
                          {accounts.map((a) => (
                            <SelectItem key={a.id} value={a.code}>
                              <span className="font-mono text-zinc-500 mr-2">{a.code}</span>
                              {a.name}
                              <span className="text-[10px] text-zinc-500 ml-2">({a.type})</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-1">
                      <Input value={l.memo || ""} onChange={(e) => updateLine(i, { memo: e.target.value })}
                             className="h-8 text-xs" data-testid={`je-line-memo-${i}`} />
                    </td>
                    <td className="px-2 py-1">
                      <Input type="number" step="0.01" value={l.debit}
                             onChange={(e) => updateLine(i, { debit: e.target.value, credit: e.target.value ? "" : l.credit })}
                             className="h-8 text-xs text-right font-mono" data-testid={`je-line-debit-${i}`} />
                    </td>
                    <td className="px-2 py-1">
                      <Input type="number" step="0.01" value={l.credit}
                             onChange={(e) => updateLine(i, { credit: e.target.value, debit: e.target.value ? "" : l.debit })}
                             className="h-8 text-xs text-right font-mono" data-testid={`je-line-credit-${i}`} />
                    </td>
                    <td className="px-2 py-1 text-right">
                      {lines.length > 2 && (
                        <button onClick={() => removeLine(i)} className="text-zinc-500 hover:text-rose-400"
                                data-testid={`je-line-remove-${i}`}>
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-white/10 text-xs bg-white/[0.02]">
                <tr>
                  <td colSpan={2} className="px-2 py-2 text-right text-zinc-400">Totals</td>
                  <td className="px-2 py-2 text-right font-mono">{money(totals.debits)}</td>
                  <td className="px-2 py-2 text-right font-mono">{money(totals.credits)}</td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan={5} className="px-2 py-2">
                    <div className="flex items-center justify-between">
                      <Button variant="ghost" size="sm" onClick={addLine} className="h-7 gap-1"
                              data-testid="je-line-add">
                        <Plus className="h-3 w-3" /> Add line
                      </Button>
                      <Badge variant="outline"
                             className={totals.balanced ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}
                             data-testid="je-balance-badge">
                        {totals.balanced ? (
                          <><CheckCircle2 className="h-3 w-3 mr-1" /> Balanced</>
                        ) : (
                          <><XCircle className="h-3 w-3 mr-1" /> Δ {money(Math.abs(totals.debits - totals.credits))}</>
                        )}
                      </Badge>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10">Cancel</Button>
          <Button onClick={submit} disabled={busy || !totals.balanced}
                  className="bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="je-form-post">
            {busy ? "Posting…" : "Post entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
